import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert, Snackbar, Stack,
  TextField, Radio, RadioGroup, FormControlLabel, Chip, CircularProgress
} from '@mui/material';
import { Print, Save } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ConfiguracionImpresora() {
  const [config, setConfig] = useState({
    tipo_ticket: '58mm',
    auto_imprimir_ticket: true,
    auto_imprimir_pdf: false,
    margen_ticket: 5,
    nombre_empresa_ticket: '',
    mensaje_pie_ticket: 'Gracias por su compra',
    mostrar_logo_ticket: false,
  });
  const [temaPOS, setTemaPOS] = useState('tema-azul');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [testPrinting, setTestPrinting] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    cargarConfig();
    // El tema es personal — se lee del localStorage
    const temaLocal = localStorage.getItem('tema_pos') || 'tema-azul';
    setTemaPOS(temaLocal);
  }, []);

  const cargarConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/config-pos`, { headers: headers() });
      if (res.data?.data) {
        setConfig(prev => ({ ...prev, ...res.data.data }));
      }
    } catch {
      // Si falla, usar valores por defecto (ya están en el state)
    }
    setLoading(false);
  };

  const guardar = async () => {
    try {
      // Guardar config de impresora en el servidor (para toda la empresa)
      await axios.put(`${API_URL}/api/config-pos`, config, { headers: headers() });

      // Guardar tema localmente (personal por usuario)
      localStorage.setItem('tema_pos', temaPOS);

      // También mantener en localStorage para acceso offline rápido del POS
      localStorage.setItem('config_impresora', JSON.stringify({ ...config, tema_pos: temaPOS }));

      setSnackbar({ open: true, message: 'Configuración guardada para toda la empresa', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error al guardar', severity: 'error' });
    }
  };

  const imprimirTicketPrueba = () => {
    setTestPrinting(true);
    const ancho = config.tipo_ticket === '58mm' ? 32 : 48;
    const linea = '='.repeat(ancho);
    const centrar = (text) => {
      const pad = Math.max(0, Math.floor((ancho - text.length) / 2));
      return ' '.repeat(pad) + text;
    };
    const derecha = (lab, val) => `${lab}${' '.repeat(Math.max(1, ancho - lab.length - val.length))}${val}`;

    const ticket = [
      centrar(config.nombre_empresa_ticket || 'MI EMPRESA'),
      centrar('RUC: 1234567890001'),
      linea,
      centrar('TICKET DE PRUEBA'),
      centrar(new Date().toLocaleString('es-EC')),
      linea,
      'Producto de prueba        $1.00',
      'Producto ejemplo x2       $2.00',
      linea,
      derecha('SUBTOTAL 0%:', '$1.00'),
      derecha('SUBTOTAL 15%:', '$2.00'),
      derecha('IVA 15%:', '$0.30'),
      linea,
      derecha('TOTAL:', '$3.30'),
      linea,
      centrar(config.mensaje_pie_ticket || 'Gracias por su compra'),
      '',
    ].join('\n');

    const ventana = window.open('', '_blank', `width=${config.tipo_ticket === '58mm' ? 220 : 302},height=600`);
    ventana.document.write(`
      <html>
      <head>
        <title>Ticket de Prueba</title>
        <style>
          @page { margin: 0; size: ${config.tipo_ticket === '58mm' ? '58mm' : '80mm'} auto; }
          body { 
            font-family: 'Courier New', monospace; 
            font-size: ${config.tipo_ticket === '58mm' ? '10px' : '12px'}; 
            margin: ${config.margen_ticket}px;
            padding: 0;
            width: ${config.tipo_ticket === '58mm' ? '48mm' : '72mm'};
          }
          pre { margin: 0; white-space: pre-wrap; word-wrap: break-word; }
        </style>
      </head>
      <body>
        <pre>${ticket}</pre>
        <script>
          window.onload = function() { window.print(); setTimeout(function(){ window.close(); }, 1000); }
        </script>
      </body>
      </html>
    `);
    ventana.document.close();
    setTestPrinting(false);
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>Configuracion de Impresoras</Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          Esta configuracion se comparte con todos los vendedores de la empresa.
          Solo el tema de color es personal.
        </Alert>

        {/* IMPRESORA DE TICKETS */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Impresora de Tickets (POS)</Typography>

          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Ancho del papel</Typography>
              <RadioGroup row value={config.tipo_ticket} onChange={(e) => setConfig({ ...config, tipo_ticket: e.target.value })}>
                <FormControlLabel value="58mm" control={<Radio />} label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>58mm</Typography>
                    <Typography variant="caption" color="text.secondary">~32 caracteres por linea</Typography>
                  </Box>
                } />
                <FormControlLabel value="80mm" control={<Radio />} label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>80mm</Typography>
                    <Typography variant="caption" color="text.secondary">~48 caracteres por linea</Typography>
                  </Box>
                } />
              </RadioGroup>
            </Box>

            <TextField fullWidth label="Nombre de la empresa (en ticket)" value={config.nombre_empresa_ticket}
              onChange={(e) => setConfig({ ...config, nombre_empresa_ticket: e.target.value.toUpperCase() })}
              placeholder="MI NEGOCIO" helperText="Se imprime en la cabecera del ticket" />

            <TextField fullWidth label="Mensaje al pie del ticket" value={config.mensaje_pie_ticket}
              onChange={(e) => setConfig({ ...config, mensaje_pie_ticket: e.target.value })}
              placeholder="Gracias por su compra" />

            <TextField type="number" label="Margen (px)" value={config.margen_ticket} sx={{ width: 150 }}
              onChange={(e) => setConfig({ ...config, margen_ticket: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0, max: 20 }} />

            <FormControlLabel
              control={<Radio checked={config.auto_imprimir_ticket} onChange={() => setConfig({ ...config, auto_imprimir_ticket: !config.auto_imprimir_ticket })} />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Imprimir ticket automaticamente al cobrar</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {config.auto_imprimir_ticket ? 'Activado' : 'Desactivado'}
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={<Radio checked={config.auto_imprimir_pdf} onChange={() => setConfig({ ...config, auto_imprimir_pdf: !config.auto_imprimir_pdf })} />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Abrir PDF automaticamente al facturar</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {config.auto_imprimir_pdf ? 'Activado' : 'Desactivado'}
                  </Typography>
                </Box>
              }
            />

            <Button variant="outlined" startIcon={<Print />} onClick={imprimirTicketPrueba} disabled={testPrinting}>
              Imprimir Ticket de Prueba
            </Button>
          </Stack>
        </Paper>

        {/* TEMA DE COLOR POS (personal por usuario) */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Tema de Color del POS</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Este ajuste es personal — cada vendedor puede elegir su tema.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {[
              { id: '', nombre: 'Verde Oscuro', color1: '#0f3d0f', color2: '#1a1a2e', accent: '#00e676' },
              { id: 'tema-negro', nombre: 'Negro', color1: '#000', color2: '#111', accent: '#fff' },
              { id: 'tema-azul', nombre: 'Azul', color1: '#002884', color2: '#0a1929', accent: '#42a5f5' },
              { id: 'tema-morado', nombre: 'Morado', color1: '#4a148c', color2: '#1a0033', accent: '#ce93d8' },
              { id: 'tema-rojo', nombre: 'Rojo', color1: '#600', color2: '#1a0000', accent: '#ef5350' },
            ].map(tema => (
              <Box key={tema.id}
                onClick={() => setTemaPOS(tema.id)}
                sx={{
                  width: 110, height: 65, borderRadius: 2, cursor: 'pointer',
                  background: `linear-gradient(135deg, ${tema.color1}, ${tema.color2})`,
                  border: temaPOS === tema.id ? `3px solid ${tema.accent}` : '3px solid transparent',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s', '&:hover': { transform: 'scale(1.05)' },
                }}>
                <Box sx={{ width: 28, height: 3, bgcolor: tema.accent, borderRadius: 1, mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, fontSize: 10 }}>{tema.nombre}</Typography>
              </Box>
            ))}
          </Stack>
        </Paper>

        {/* RESUMEN */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fafafa' }}>
          <Typography variant="subtitle2" gutterBottom>Resumen:</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Ticket: ${config.tipo_ticket}`} color="primary" size="small" />
            <Chip label={config.auto_imprimir_ticket ? 'Auto-ticket: ON' : 'Auto-ticket: OFF'} color={config.auto_imprimir_ticket ? 'success' : 'default'} size="small" />
            <Chip label={config.auto_imprimir_pdf ? 'Auto-PDF: ON' : 'Auto-PDF: OFF'} color={config.auto_imprimir_pdf ? 'success' : 'default'} size="small" />
            <Chip label="Compartido con toda la empresa" color="info" size="small" variant="outlined" />
          </Stack>
        </Paper>

        {/* GUARDAR */}
        <Button variant="contained" size="large" startIcon={<Save />} onClick={guardar} sx={{ px: 4, py: 1.5 }}>
          GUARDAR CONFIGURACION
        </Button>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
